import { useEffect, useState } from "react";
import { UserPlus, Trash2, Users2, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const EMPTY = { email: "", name: "", password: "", role: "member" };

export default function Team() {
  const { user } = useAuth();
  const { current, currentId } = useCompany();
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const myRole = members.find((m) => m.id === user?.id)?.role;
  const isOwner = myRole === "owner";

  const load = () => api.get("/team").then((r) => setMembers(r.data));
  useEffect(() => {
    if (currentId) load();
  }, [currentId]);

  const initials = (name) =>
    (name || "?")
      .split(" ")
      .map((s) => s[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const add = async () => {
    if (!form.email.trim()) return toast.error("Email is required");
    setSaving(true);
    try {
      const res = await api.post("/team", form);
      toast.success(res.data.existing ? "Existing user granted access" : "New user created & added");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add user");
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (m, role) => {
    try {
      await api.put(`/team/${m.id}`, { role });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change role");
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name}'s access to ${current?.name}?`)) return;
    try {
      await api.delete(`/team/${m.id}`);
      toast.success("Access removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove user");
    }
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Users &amp; Access</h1>
          <p className="text-slate-500 text-sm mt-1">
            People who can access <span className="font-medium text-slate-700">{current?.name}</span>. Owners can add teammates (e.g. your accounting group).
          </p>
        </div>
        {isOwner && (
          <Button data-testid="add-team-btn" onClick={() => { setForm(EMPTY); setOpen(true); }} className="rounded-md bg-slate-900 hover:bg-slate-800">
            <UserPlus className="h-4 w-4 mr-2" /> Add User
          </Button>
        )}
      </div>

      <Card className="rounded-lg border-slate-200 shadow-sm overflow-hidden p-0">
        <table className="w-full text-sm" data-testid="team-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
              <th className="px-5 py-3 font-medium">User</th>
              <th className="px-5 py-3 font-medium">Email</th>
              <th className="px-5 py-3 font-medium">Role</th>
              <th className="px-5 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`team-row-${m.id}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-slate-900 text-white text-xs">{initials(m.name)}</AvatarFallback>
                    </Avatar>
                    <span className="font-medium text-slate-900">
                      {m.name}
                      {m.id === user?.id && <span className="text-slate-400 font-normal"> (you)</span>}
                    </span>
                  </div>
                </td>
                <td className="px-5 py-3 text-slate-600">{m.email}</td>
                <td className="px-5 py-3">
                  {isOwner && m.id !== user?.id ? (
                    <Select value={m.role} onValueChange={(v) => changeRole(m, v)}>
                      <SelectTrigger data-testid={`role-select-${m.id}`} className="h-8 w-32 rounded-md capitalize">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="owner">Owner</SelectItem>
                        <SelectItem value="member">Member</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant="outline" className={`capitalize gap-1 ${m.role === "owner" ? "bg-slate-900 text-white border-slate-900" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                      {m.role === "owner" && <ShieldCheck className="h-3 w-3" />}
                      {m.role}
                    </Badge>
                  )}
                </td>
                <td className="px-5 py-3 text-right">
                  {isOwner && m.id !== user?.id && (
                    <Button variant="ghost" size="icon" data-testid={`remove-team-${m.id}`} onClick={() => remove(m)}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {members.length === 0 && (
          <div className="p-16 text-center">
            <Users2 className="h-8 w-8 mx-auto text-slate-300" />
            <p className="text-slate-400 mt-3 text-sm">No users yet.</p>
          </div>
        )}
      </Card>

      {!isOwner && (
        <p className="text-xs text-slate-400 mt-3">You have member access — only owners can add or remove users.</p>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading">Add User to {current?.name}</DialogTitle>
            <DialogDescription>
              If the email already has an account, they're simply granted access. Otherwise a new user is created with the password you set.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input data-testid="team-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 rounded-md" placeholder="accountant@firm.com" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Name (new users)</Label>
                <Input data-testid="team-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-md" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="team-role-select" className="mt-1 rounded-md capitalize">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="owner">Owner</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Temporary Password (new users only)</Label>
              <Input data-testid="team-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-md" placeholder="Leave blank if the user already exists" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-md" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-team-btn" onClick={add} disabled={saving} className="rounded-md bg-slate-900 hover:bg-slate-800">
              {saving ? "Adding…" : "Add User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
